import type { ObjectId } from 'mongodb';

import type { Meta } from './Entities';
import type { IncomingInvoiceLineItem } from './IncomingInvoices';

export enum DirectPayResponseStatus {
  failed = 'failed',
  succeeded = 'succeeded',
  skipped = 'skipped',
  duplicate = 'duplicate',
  nonReplayableError = 'non-replayable-error',
}

export enum DirectPayEventType {
  createDirectPayProject = 'createDirectPayProject',
  getDirectPayInvoiceDocs = 'getDirectPayInvoiceDocs',
  getDirectPayProjectDetails = 'getDirectPayProjectDetails',
  sendDirectPayProjectAdjustment = 'sendDirectPayProjectAdjustment',
  updateDirectPayProject = 'updateDirectPayProject',
  sendProjectAdjustment = 'sendProjectAdjustment',
  getInvoiceDocuments = 'getInvoiceDocuments',
}

export interface CEDIncomingInvoiceLineItem {
  lineType: string;
  extendedPrice?: number;
  productCode: string;
  quantityOrdered: number;
  quantityShipped?: number;
  productDescription: string;
  mfrName?: string;
  mfrCode?: string; // not doing anything with this yet, but there is some potential to use it to validate the system design
}

export interface CEDInvoice {
  number: string;
  date: string;
  amount: number;
  lineItems?: CEDIncomingInvoiceLineItem[];
}

export interface CEDIncomingInvoicesPayload {
  identifier: string;
  totalMaterialsAmount: number;
  finalizedDate: string;
  invoices: CEDInvoice[];
}

export interface CEDIncomingShipmentConfirmationPayload {
  identifier: string;
  shipConfirmationDate: string;
}

// Gotta love the mixture of camel and pascal case
export interface CEDProjectDetails {
  identifier: string;
  alphaID: string;
  installerNameOrDBA: string;
  ProjectName: string;
  ProjectAddress: string;
  Status: string;
  SystemSize: string;
  LoanAmount: number;
  LoanOriginationDate: string;
  TotalReceived?: number;
  ReleaseDate?: string;
  invoices: Omit<CEDIncomingInvoicesPayload, 'identifier'>[];
}

export interface SunrgyGetProjectDetailsResponse {
  project: SunrgyGetProjectDetails;
  status: string;
  code: string;
}
export interface SunrgyGetProjectDetails {
  identifier: string;
  installerIdentifier: string;
  customerName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  projectCost: number;
  systemSize: number;
  lrStatus?: string;
  ntpDate?: string;
}

export interface CEDInvoicePdf {
  number: string;
  amount: number;
  date: string;
  file: string;
  lineItems?: CEDIncomingInvoiceLineItem[];
}
export interface CEDGetInvoicesResponse {
  identifier: string;
  invoices: CEDInvoicePdf[];
}

export interface StandardGetProjectInvoicesRes {
  status: string;
  code: string;
  invoices: {
    projectIdentifier: string;
    vendorIdentifier: string;
    finalizationDate: string;
    totalInvoicedAmount: number;
    invoices: Array<{
      invoiceNumber: string;
      invoiceName: string;
      invoiceDate: string;
      totalAmount: number;
      lineItems: Array<{
        manufacturerCode: string;
        manufacturerName: string;
        productCode: string;
        description: string;
        quantity: number;
        unitPrice: number;
        totalAmount: number;
      }>;
    }>;
  };
}

export interface formattedDirectPayInvoicesRes {
  invoices: Array<{
    invoiceNumber: string;
    invoiceDate: string;
    totalAmount: number;
    lineItems?: IncomingInvoiceLineItem[];
    file?: string;
  }>;
}

export interface SunrgyGetInvoiceDocumentRes {
  invoice_html: string;
}

export interface DirectPayLogsDocument {
  id: ObjectId;
  accountId: ObjectId;
  event: DirectPayEventType;
  status: string;
  request: Record<string, any>;
  response?: Record<string, any>;
  message?: string;
  directPayMaterialVendorAlias?: DirectPayMaterialVendorAliasOptions;
  isOptIn?: boolean;
  subCompanyId?: string;
  meta: Meta;
}

export enum DirectPayMaterialVendorAliasOptions {
  ced = 'ced',
  sunrgy = 'sunrgy',
  baywa = 'baywa',
  sonepar = 'sonepar',
  usesi = 'usesi',
  soligent = 'soligent',
  wesco = 'wesco',
}

export enum DirectPayMaterialVendorDisplayNameOptions {
  ced = 'Greentech-CED',
  sunrgy = 'Sunrgy',
  baywa = 'Baywa',
  sonepar = 'Sonepar',
  usesi = 'US Electric Services Inc.',
  soligent = 'Soligent',
  wesco = 'Wesco',
}
export interface DirectPayInvoiceLineItem {
  productCode: string;
  description: string;
  quantity: number;
  manufacturerName: string;
  manufacturerCode?: string;
  unitPrice: number;
  totalAmount: number;
}

export interface DirectPayInvoice {
  invoiceId?: string;
  customerNumber?: string; // only for Wesco
  invoiceNumber: string;
  invoiceDate: string;
  invoiceStatus: string;
  totalAmount: number;
  lineItems?: DirectPayInvoiceLineItem[];
}

export interface DirectPayInvoicesPayload {
  projectIdentifier: string;
  vendorIdentifier: string;
  customerNumber?: string; // only for Wesco
  subCompany?: string; // required for only vendors who have subCompanies under them (i.e Sonepar)
  totalInvoicedAmount: number;
  finalizationDate: string;
  invoices: DirectPayInvoice[];
}

export interface FormattedCreateProjectRes {
  success: boolean;
  projectIdentifier: string;
  message?: string;
  directPayMaterialVendorAlias: DirectPayMaterialVendorAliasOptions;
  projectId?: string;
}

export interface CEDCreateProjectRes {
  success: boolean;
  message: string;
  projectId: string;
  installerId: string;
}

export interface SunrgyCreateProjectRes {
  status: string;
  code: string;
  projectIdentifier: string;
}

export enum DirectPayProjectStatusForVendor {
  active = 'active',
  optOut = 'opt-out',
  cancelled = 'cancelled',
  onHold = 'onHold',
  installSubmitted = 'install-submitted',
  permissionToOperate = 'permission-to-operate',
  substantialCompletion = 'substantial-completion',
}
